/**
 * "Offboarding a client" tour.
 *
 * Walks through the offboarding flow — picking a client, opening the
 * Offboarding section, hitting Offboard Client, completing the modal, and
 * the consequences (status pill, retention period, audit trail).
 *
 * Targets:
 *   tr.clickable                          — any client row
 *   data-tour="section-tab-offboarding"   — Offboarding section nav button
 *   data-tour="offboard-btn"              — primary "Offboard Client" button (mgmt only)
 *   data-tour="confirm-offboarding"       — "Confirm Offboarding" button in modal
 *
 * Reason values mirror ClientOffboarding.vue's `reasons` array (lines 96-103).
 * Roles mirror the `isManagement` gate (line 27). Keep in sync.
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists, advanceWhenAppears } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const offboardingTour = {
    id: 'offboarding',
    title: 'Offboarding a client',
    description: 'How to formally end a client relationship — when to do it, the reason categories, and what happens to the record afterwards.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Offboarding a client',
            text: `Offboarding marks a client relationship as formally ended. The record
                stays on file (7-year retention is required), but the client is no longer
                considered active and won't appear in your normal day-to-day views.
                <br><br>
                <strong>Roles:</strong> only the Compliance Officer or Senior Manager can
                offboard a client. Staff members will see an info banner instead of the
                offboard button.`,
            buttons: [START],
        },
        {
            id: 'pick-row',
            attachTo: { element: 'table.aml_table tbody tr.clickable', on: 'top' },
            beforeShowPromise: () => waitForElement('table.aml_table tbody tr.clickable'),
            title: 'Open a client',
            text: `Click any row to open the client's detail page. Offboarding happens
                per-client.`,
            // See editClient.js for why we watch for the destination
            // marker instead of relying on advanceOn-on-row-click.
            when: advanceWhenAppears('[data-tour="section-tab-offboarding"]'),
        },
        {
            id: 'section-tab',
            attachTo: { element: '[data-tour="section-tab-offboarding"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="section-tab-offboarding"]'),
            title: 'Offboarding section',
            text: `Click <strong>Offboarding</strong> in the section nav to scroll to
                that block. Once there, you'll see the offboarding controls (or a
                role-restriction notice for non-management users).`,
            advanceOn: { selector: '[data-tour="section-tab-offboarding"]', event: 'click' },
        },
        {
            id: 'offboard-btn',
            attachTo: { element: '[data-tour="offboard-btn"]', on: 'top' },
            showOn: targetExists('[data-tour="offboard-btn"]'),
            beforeShowPromise: () => waitForElement('[data-tour="offboard-btn"]'),
            title: 'Click "Offboard Client"',
            text: `The red <strong>Offboard Client</strong> button opens the offboarding
                modal. Before you click, the platform reminds you that all records will
                be retained for the 7-year compliance period.`,
            advanceOn: { selector: '[data-tour="offboard-btn"]', event: 'click' },
        },
        {
            id: 'staff-notice',
            showOn: () => !document.querySelector('[data-tour="offboard-btn"]'),
            title: 'You\'re not authorised to offboard',
            text: `Only Compliance Officers and Senior Managers can offboard clients. As
                a staff user you'll see an info banner here instead of the Offboard
                button. Speak to your CO/SM if a client needs offboarding.
                <br><br>
                The remaining tour steps describe what happens once an authorised user
                clicks Offboard Client.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'modal-warnings',
            attachTo: { element: '[data-tour="offboard-warning"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="confirm-offboarding"]'),
            // Cancel if the modal closes (X or Cancel) — confirm-offboarding
            // only exists while the modal is open.
            cancelIfMissing: '[data-tour="confirm-offboarding"]',
            title: 'Outstanding-matters check',
            text: `The modal opens with a red <strong>warning banner</strong> reminding
                you the action is significant. If the client has any of the following
                still open, an extra <em>amber</em> warning lists them:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Open escalations</li>
                    <li>Active or pending designated services</li>
                    <li>In-progress CDD</li>
                </ul>
                When you proceed, the platform automatically tidies these up:
                <strong>active services</strong> are marked Terminated, <strong>open
                escalations</strong> are force-closed, and <strong>associated
                persons</strong> are marked Departed. The amber banner makes sure
                you've seen what'll be cascaded before you commit.`,
            // No Previous button here — the prior tour step lives outside
            // the modal that the user has just opened. Going back to it
            // while the modal is still on screen reads as broken.
            buttons: [NEXT],
        },
        {
            id: 'reason',
            attachTo: { element: '[data-tour="offboard-reason-field"]', on: 'right' },
            cancelIfMissing: '[data-tour="confirm-offboarding"]',
            title: 'Pick a reason',
            text: `<strong>Reason for Offboarding</strong> (required) is a fixed list:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Client request — relationship ended</li>
                    <li>Services concluded — all matters resolved</li>
                    <li>Risk appetite exceeded — practice declined to continue</li>
                    <li>Sanctions or compliance action required cessation</li>
                    <li>Client uncontactable / unable to complete CDD</li>
                    <li>Other — requires you to type a free-text reason</li>
                </ul>
                Add free-text <strong>Notes</strong> below if you want to record extra
                context (recommended).`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'confirm',
            attachTo: { element: '[data-tour="confirm-offboarding"]', on: 'top' },
            cancelIfMissing: '[data-tour="confirm-offboarding"]',
            title: 'Confirm Offboarding',
            text: `Click <strong>Confirm Offboarding</strong> to finalise. The client's
                status pill flips to <em>Offboarded</em>, the relationship is locked, and
                this section now shows a read-only summary of when they were offboarded
                and why.
                <br><br>
                CO, SM and GB are notified of the offboarding, and the action is recorded
                on the audit trail.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'goodbye-letter',
            title: 'The Goodbye Letter (when applicable)',
            text: `When the offboarding reason is <em>Client request — relationship
                ended</em> or <em>Services concluded — all matters resolved</em>,
                the platform offers to send a <strong>Goodbye Letter</strong> to
                the client immediately after confirmation.
                <br><br>
                A short modal lets you preview, edit, and send the letter (or skip
                it). The letter is a courtesy — it formally acknowledges the end
                of the relationship and confirms that records will be retained for
                the 7-year period. Sending is logged on the client's compliance
                trail.`,
            buttons: [BACK, DONE],
        },
    ],
};
